import { useEffect, useState, useCallback } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './stores/authStore';
import { useClipboardStore } from './stores/clipboardStore';
import { useChatStore } from './stores/chatStore';
import { useBillingStore } from './stores/billingStore';
import { usePWAStore } from './stores/pwaStore';
import { setTheme, setFont } from './utils/theme';
import Sidebar from './components/Sidebar';
import ChatView from './components/ChatView';
import ClipboardView from './components/ClipboardView';
import TrashView from './components/TrashView';
import AuthPage from './components/AuthPage';
import SettingsModal from './components/settings/SettingsModal';
import DenominationalDocumentsView from './components/DenominationalDocumentsView';
import FileUploadView from './components/FileUploadView';
import FolderManagementView from './components/FolderManagementView';
import ErrorBoundary from './components/ErrorBoundary';
import { GracePeriodBanner } from './components/subscription/GracePeriodBanner';
import ChatManageView from './components/ChatManageView';
import SubscriptionPlans from './pages/SubscriptionPlans';
import Onboarding from './pages/Onboarding';
import PublicPricing from './pages/PublicPricing';
import ResetPassword from './pages/ResetPassword';
import VerifyEmailChange from './pages/VerifyEmailChange';
import SetPassword from './pages/SetPassword';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

type View = 'chat' | 'clipboard' | 'trash' | 'documents' | 'file-upload' | 'folders' | 'subscription' | 'chat-manage';

function App() {
  const location = useLocation();
  const { isAuthenticated, isLoading, loadUser } = useAuthStore();
  const { connectWebSocket, disconnectWebSocket } = useClipboardStore();
  const { currentConversation, createConversation } = useChatStore();
  const { loadSubscription } = useBillingStore();
  const { initializePWA } = usePWAStore();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentView, setCurrentView] = useState<View>('chat');

  // Initialize PWA event listeners on mount
  useEffect(() => {
    initializePWA();
  }, [initializePWA]);

  // Handle view switching when sending message from clipboard view
  const handleViewChange = useCallback(async (view: View, options?: { createNewChat?: boolean }) => {
    if (view === 'chat' && options?.createNewChat) {
      // Create new conversation before switching to chat view
      await createConversation('New Chat');
    }
    setCurrentView(view);
  }, [createConversation]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Load subscription status and apply saved theme when user authenticates
  useEffect(() => {
    if (isAuthenticated) {
      loadSubscription();
      // Apply user's saved appearance preferences
      const user = useAuthStore.getState().user;
      setTheme(user?.color_mode || 'dark');
      setFont(user?.chat_font || 'system');
    }
  }, [isAuthenticated, loadSubscription]);

  // Listen for navigation events from child components (e.g., UpgradePrompt)
  useEffect(() => {
    const handler = (e: Event) => {
      const view = (e as CustomEvent).detail as View;
      if (view) setCurrentView(view);
    };
    window.addEventListener('navigate-view', handler);
    return () => window.removeEventListener('navigate-view', handler);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const token = localStorage.getItem('access_token');
      if (token) {
        connectWebSocket(token);
      }
    } else {
      disconnectWebSocket();
    }

    return () => {
      disconnectWebSocket();
    };
  }, [isAuthenticated, connectWebSocket, disconnectWebSocket]);

  // Check for public routes that don't require authentication
  const isOnboardingRoute = location.pathname === '/onboarding';
  const isPricingRoute = location.pathname === '/pricing';
  const isResetPasswordRoute = location.pathname === '/reset-password';
  const isVerifyEmailChangeRoute = location.pathname === '/verify-email-change';
  const isSetPasswordRoute = location.pathname === '/set-password';
  const isLoginRoute = location.pathname === '/login';
  const isPrivacyRoute = location.pathname === '/privacy';
  const isTermsRoute = location.pathname === '/terms';

  // Show privacy policy page (public route)
  if (isPrivacyRoute) {
    return (
      <ErrorBoundary>
        <PrivacyPolicy />
      </ErrorBoundary>
    );
  }

  // Show terms of service page (public route)
  if (isTermsRoute) {
    return (
      <ErrorBoundary>
        <TermsOfService />
      </ErrorBoundary>
    );
  }

  // Show onboarding page without authentication check
  if (isOnboardingRoute) {
    return (
      <ErrorBoundary>
        <Onboarding />
      </ErrorBoundary>
    );
  }

  // Show pricing page for new users without authentication
  if (isPricingRoute) {
    return (
      <ErrorBoundary>
        <PublicPricing />
      </ErrorBoundary>
    );
  }

  // Show reset password page (public route from email link)
  if (isResetPasswordRoute) {
    return (
      <ErrorBoundary>
        <ResetPassword />
      </ErrorBoundary>
    );
  }

  // Show verify email change page (public route from email link)
  if (isVerifyEmailChangeRoute) {
    return (
      <ErrorBoundary>
        <VerifyEmailChange />
      </ErrorBoundary>
    );
  }

  // Show set password page (public route from email link for VA members)
  if (isSetPasswordRoute) {
    return (
      <ErrorBoundary>
        <SetPassword />
      </ErrorBoundary>
    );
  }

  if (isLoading) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-dark-bg">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-accent-blue border-t-transparent rounded-full animate-spin"></div>
          <div className="text-dark-text text-xl">Loading PARACLETE...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <ErrorBoundary><AuthPage /></ErrorBoundary>;
  }

  return (
    <ErrorBoundary>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#1e293b',
            color: '#f1f5f9',
            border: '1px solid #334155',
          },
          success: {
            iconTheme: {
              primary: '#3b82f6',
              secondary: '#f1f5f9',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#f1f5f9',
            },
          },
        }}
      />

      {/* Grace Period Warning Banner */}
      <GracePeriodBanner
        onUpdatePayment={() => handleViewChange('subscription')}
        dismissible={true}
      />

      <div className="w-screen h-screen flex bg-dark-bg text-dark-text">
        {/* Hamburger menu button - mobile only */}
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="md:hidden fixed z-50 p-3 bg-dark-card/90 backdrop-blur-sm border border-dark-border rounded-lg text-dark-text hover:bg-dark-sidebar active:bg-dark-sidebar transition-colors shadow-lg min-w-[48px] min-h-[48px] flex items-center justify-center"
          style={{ top: 'max(1rem, env(safe-area-inset-top))', left: 'max(1rem, env(safe-area-inset-left))' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>

        <Sidebar
          isMobileOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          currentView={currentView}
          onViewChange={(view) => handleViewChange(view)}
        />
        {currentView === 'chat' ? (
          <ChatView key="chat" />
        ) : currentView === 'clipboard' ? (
          <ClipboardView
            key="clipboard"
            onSwitchToChat={() => handleViewChange('chat', { createNewChat: !currentConversation })}
          />
        ) : currentView === 'documents' ? (
          <DenominationalDocumentsView key="documents" />
        ) : currentView === 'file-upload' ? (
          <FileUploadView key="file-upload" />
        ) : currentView === 'folders' ? (
          <FolderManagementView key="folders" />
        ) : currentView === 'subscription' ? (
          <SubscriptionPlans key="subscription" />
        ) : currentView === 'chat-manage' ? (
          <ChatManageView key="chat-manage" onViewChange={(view) => handleViewChange(view as View)} />
        ) : (
          <TrashView
            key="trash"
            onSwitchToChat={() => handleViewChange('chat', { createNewChat: !currentConversation })}
          />
        )}
      </div>

      {/* Settings Modal */}
      <SettingsModal />
    </ErrorBoundary>
  );
}

export default App;
